import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, X, ShieldCheck, User as UserIcon, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/admin/AuthContext";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const empty = { email: "", password: "", name: "", role: "editor" };

export const UsersManager = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(empty);
  const [resetting, setResetting] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("Usuário criado"); setCreating(false); setForm(empty); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Excluir "${u.email}"?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Excluído"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Mínimo 6 caracteres");
    await api.put(`/admin/users/${resetting.id}`, { password: newPw });
    toast.success("Senha redefinida"); setResetting(null); setNewPw("");
  };

  return (
    <div data-testid="admin-users">
      <PageTitle title="Usuários" subtitle={`${users.length} usuários`}
        action={<GoldButton onClick={() => { setForm(empty); setCreating(true); }} data-testid="user-add"><Plus className="w-4 h-4 inline mr-1" /> Novo Usuário</GoldButton>} />
      <Card className="!p-0 overflow-hidden">
        <div className="divide-y divide-white/[0.05]">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`user-${u.id}`}>
              <div className="w-10 h-10 rounded-full bg-[#F5C542]/10 border border-[#F5C542]/25 flex items-center justify-center">
                {u.role === "super_admin" ? <ShieldCheck className="w-5 h-5 text-[#F5C542]" /> : <UserIcon className="w-5 h-5 text-white/60" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate">{u.name || u.email}</div>
                <div className="text-xs text-white/40 truncate">{u.email}</div>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full border ${u.role === "super_admin" ? "text-[#F5C542] border-[#F5C542]/30 bg-[#F5C542]/10" : "text-white/60 border-white/15 bg-white/5"}`}>
                {u.role === "super_admin" ? "Super Admin" : u.role}
              </span>
              <div className="flex gap-1">
                <button onClick={() => { setResetting(u); setNewPw(""); }} className="p-2 text-white/60 hover:text-[#F5C542]" title="Redefinir senha"><KeyRound className="w-4 h-4" /></button>
                {u.role !== "super_admin" && (
                  <button onClick={() => remove(u)} className="p-2 text-white/60 hover:text-[#FF3B30]" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <AnimatePresence>
        {creating && (
          <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setCreating(false)} />
            <motion.form onSubmit={create} initial={{ scale: 0.94 }} animate={{ scale: 1 }} exit={{ scale: 0.94 }} className="relative glass-gold w-full max-w-md p-6" data-testid="user-modal">
              <button type="button" onClick={() => setCreating(false)} className="absolute top-4 right-4 text-white/50"><X className="w-5 h-5" /></button>
              <h3 className="font-display text-xl font-bold text-white mb-5">Novo Usuário</h3>
              <div className="space-y-4">
                <Field label="Nome"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
                <Field label="E-mail"><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="user-email" /></Field>
                <Field label="Senha" hint="Mínimo 6 caracteres"><input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="user-password" /></Field>
                <Field label="Função">
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </Field>
              </div>
              <div className="flex gap-3 mt-6">
                <GoldButton type="submit" className="flex-1" data-testid="user-save">Criar</GoldButton>
                <GhostButton type="button" onClick={() => setCreating(false)}>Cancelar</GhostButton>
              </div>
            </motion.form>
          </motion.div>
        )}
        {resetting && (
          <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setResetting(null)} />
            <motion.div initial={{ scale: 0.94 }} animate={{ scale: 1 }} className="relative glass-gold w-full max-w-sm p-6">
              <h3 className="font-display text-lg font-bold text-white mb-4">Redefinir senha — {resetting.email}</h3>
              <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nova senha" className={inputCls} />
              <div className="flex gap-3 mt-5">
                <GoldButton onClick={resetPw} className="flex-1">Salvar</GoldButton>
                <GhostButton onClick={() => setResetting(null)}>Cancelar</GhostButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
